import type { ContextEntity } from 'config';
import type { LucideProps } from 'lucide-react';
import { i18n } from '~/lib/i18n';
import router from '~/lib/router';
import { useDialoger } from '~/modules/common/dialoger/use-dialoger';
import UnsavedBadge from '~/modules/common/unsaved-badge';
import CreateOrganizationForm from '~/modules/organizations/create-organization-form';
import { organizationsKeys } from '~/modules/organizations/query';
import type { Organization } from '~/modules/organizations/types';
import CreateWorkspaceForm from '~/modules/workspaces/create-workspace-form';
import { workspacesKeys } from '~/modules/workspaces/query';
import type { Workspace } from '~/modules/workspaces/types';

type SectionsSchema = {
  label: string;
  createAction?: () => void;
  icon?: React.ElementType<LucideProps>;
};

const createOrganizationAction = () => {
  const callback = (createdOrganization: Organization) => {
    useDialoger.getState().remove('create-organization');
    router.navigate({ to: '/$idOrSlug/members', params: { idOrSlug: createdOrganization.slug } });
  };

  return useDialoger.getState().create(<CreateOrganizationForm dialog callback={callback} />, {
    className: 'md:max-w-2xl',
    id: 'create-organization',
    title: i18n.t('common:create_resource', { resource: i18n.t('common:organization').toLowerCase() }),
    titleContent: <UnsavedBadge title={i18n.t('common:create_resource', { resource: i18n.t('common:organization').toLowerCase() })} />,
  });
};

const createWorkspaceAction = () => {
  const callback = (createdWorkspace: Workspace) => {
    useDialoger.getState().remove('create-workspace');
    router.navigate({
      to: '/$orgIdOrSlug/workspaces/$idOrSlug/board',
      params: { idOrSlug: createdWorkspace.slug, orgIdOrSlug: createdWorkspace.organizationId },
    });
  };

  return useDialoger.getState().create(<CreateWorkspaceForm dialog callback={callback} />, {
    className: 'md:max-w-2xl',
    id: 'create-workspace',
    title: i18n.t('common:create_resource', { resource: i18n.t('app:workspace').toLowerCase() }),
    titleContent: <UnsavedBadge title={i18n.t('common:create_resource', { resource: i18n.t('app:workspace').toLowerCase() })} />,
  });
};

export const menuSectionsSchemas: Partial<Record<ContextEntity, SectionsSchema>> = {
  organization: { createAction: createOrganizationAction, label: 'common:organizations' },
  workspace: { createAction: createWorkspaceAction, label: 'app:workspaces' },
};

export const contextEntityCacheKeys = {
  organization: organizationsKeys.single.base(),
  workspace: workspacesKeys.single.base(),
  project: workspacesKeys.single.base(),
};
